import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/base/Seo";
import { useAuth } from "@/context/AuthContext";
import { firstProblem, validateContactFields } from "@/utils/validation";
import { SITE } from "@/config/site";

const perks = [
  { icon: "ri-store-2-line", text: "Create and edit your business listing" },
  { icon: "ri-line-chart-line", text: "Track profile views and enquiries" },
  { icon: "ri-star-line", text: "Collect and reply to customer reviews" },
  { icon: "ri-vip-crown-line", text: "Upgrade to featured placement anytime" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", password: "", confirm: "" });
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Please choose a password with at least 8 characters.");
      return;
    }
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      setError("Password must include at least one letter and one number.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!agreed) {
      setError("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    const contactProblem = firstProblem(
      validateContactFields({ email: form.email, phone: form.phone, emailRequired: true, phoneRequired: true }),
    );
    if (contactProblem) {
      setError(contactProblem);
      return;
    }
    setSubmitting(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        password: form.password,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create your account right now.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

  return (
    <>
      <Seo
        title="Create an Owner Account"
        description="Create a free owner account on Mysore Yellow Pages to list your business and manage your presence across Mysore, Mandya, Chamarajanagar and Kodagu."
        path="/register"
      />
      <div className="flex min-h-screen">
        <div className="hidden lg:flex lg:w-1/2 relative">
          <img
            src="https://readdy.ai/api/search-image?query=Storefront%20of%20a%20small%20Indian%20local%20business%20at%20dusk%20with%20warm%20glowing%20signage%2C%20amber%20and%20teal%20moody%20tones%2C%20cinematic%20editorial%20photography%2C%20soft%20bokeh&width=1200&height=1400&seq=myyp-register-bg&orientation=portrait"
            alt="Local business storefront"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/50 to-foreground-950/35"></div>
          <div className="relative z-10 flex flex-col justify-between p-10 text-background-50">
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
                <i className="ri-book-2-line text-xl"></i>
              </span>
              <span className="font-heading text-lg font-extrabold">{SITE.name}</span>
            </Link>
            <div>
              <h2 className="font-heading text-3xl font-extrabold leading-tight">
                Get your business found by local customers
              </h2>
              <ul className="mt-5 space-y-3">
                {perks.map((p) => (
                  <li key={p.text} className="flex items-center gap-3 text-sm text-background-50/90">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-background-50/15">
                      <i className={p.icon}></i>
                    </span>
                    {p.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex w-full lg:w-1/2 items-center justify-center bg-background-50 px-4 py-12">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-8 flex items-center gap-2 lg:hidden">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
                <i className="ri-book-2-line text-lg"></i>
              </span>
              <span className="font-heading text-base font-extrabold text-foreground-950">{SITE.name}</span>
            </Link>

            <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Create your account</h1>
            <p className="mt-2 text-sm text-foreground-600">
              Already registered?{" "}
              <Link to="/login" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                Sign in
              </Link>
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label htmlFor="name" className={labelClass}>Full name</label>
                <input id="name" name="name" required value={form.name} onChange={update("name")} placeholder="Your name" className={inputClass} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className={labelClass}>Email</label>
                  <input id="email" name="email" type="email" required autoComplete="email" value={form.email} onChange={update("email")} placeholder="you@business.com" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>Phone</label>
                  <input id="phone" name="phone" required value={form.phone} onChange={update("phone")} placeholder="+91 …" className={inputClass} />
                </div>
              </div>
              <div>
                <label htmlFor="company" className={labelClass}>Business name</label>
                <input id="company" name="company" required value={form.company} onChange={update("company")} placeholder="e.g. Chamundi Traders" className={inputClass} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className={labelClass}>Password</label>
                  <input id="password" name="password" type="password" required value={form.password} onChange={update("password")} placeholder="Min. 8 characters" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="confirm" className={labelClass}>Confirm password</label>
                  <input id="confirm" name="confirm" type="password" required value={form.confirm} onChange={update("confirm")} placeholder="Repeat password" className={inputClass} />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
                  <span>{error}</span>
                </div>
              )}

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-background-300 accent-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1"
                />
                <span className="text-xs leading-relaxed text-foreground-600">
                  I agree to the{" "}
                  <Link to="/terms-of-service" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">Privacy Policy</Link>.
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {submitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-user-add-line"></i>}
                {submitting ? "Creating account…" : "Create account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}